import {
  ArrowBack,
  ArrowForward,
  BarChartOutlined,
  DownloadRounded,
  InfoOutlined,
  SsidChartOutlined,
} from '@mui/icons-material'
import { Spin } from 'antd'
import { type FC, memo, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from 'react-query'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import { Popover } from 'sutra/components/Popover/Popover'
import { Radio } from 'sutra/components/Radio/Radio'
import { RadioGroup } from 'sutra/components/Radio/RadioGroup'

import ChartV2 from 'components/chart-v2/ChartV2'
import ChartInfoModal from 'components/chart-v2/components/ChartInfoModal'
import {
  type BaseChartIdentifier,
  type ChartConstraint,
  type ChartFilter,
  ChartType,
  DataPoint,
} from 'components/chart-v2/model'
import { generateOptimalColors } from 'components/chart-v2/utils'
import { useProjectStore } from 'features/project/store'
import mixpanel from 'trackers/mixpanel'
import { ProjectState } from 'types/project'

// Type for generic JSON value
type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue }

// Utility function to recursively convert camelCase keys to snake_case
const convertToSnakeCase = (obj: JsonValue): JsonValue => {
  if (obj === null || typeof obj !== 'object') {
    return obj
  }

  if (Array.isArray(obj)) {
    return obj.map(convertToSnakeCase)
  }

  const converted: { [key: string]: JsonValue } = {}
  for (const [key, value] of Object.entries(obj)) {
    const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase()
    converted[snakeKey] = convertToSnakeCase(value as JsonValue)
  }
  return converted
}

// Type for chart identifier
interface ChartIdentifier extends BaseChartIdentifier {
  domains: Array<{
    fn?: string
    args?: (string | { dimension?: string; keys?: string[] })[]
  }>
  aggregatedValues: Array<{
    fn: string
    args: (string | { dimension?: string; keys?: string[] })[]
  }>
  constraints: ChartConstraint[]
}

interface MultiDomainChartProps {
  identifier: ChartIdentifier
  data: DataPoint[]
  title?: string
  type?: ChartType
  aggregatedValues?: Array<{ fn: string; args: string[] }>
  conversationUuid: string | undefined
  messageUuid: string
  filters?: ChartFilter[]
  fetchChart: (projectId: string, identifier: JsonValue) => Promise<unknown>
}
// Dynamically determine available metrics from aggregated values or data structure
// Function argument type for nested structures
type NestedArg =
  | string
  | { dimension?: string; keys?: string[]; args?: NestedArg[] }

const getAvailableMetrics = (
  aggregatedValues?: Array<{ fn: string; args: NestedArg[] }>,
): { label: string; key: string; index: number }[] => {
  if (aggregatedValues) {
    return aggregatedValues.map((agg, index) => {
      // First try the direct path (most common case)
      let rawMetric = agg.args[0]

      // If that's an object with args (nested structure), go deeper
      if (typeof rawMetric === 'object' && rawMetric?.args) {
        rawMetric = rawMetric.args[0]
      }

      let metric = 'unknown_metric'

      // Handle dimension objects (e.g., {"dimension": "post"})
      if (typeof rawMetric === 'object' && rawMetric?.dimension) {
        metric = rawMetric.dimension
      } else if (
        typeof rawMetric === 'object' &&
        rawMetric?.keys &&
        rawMetric.keys.length > 0
      ) {
        // Handle custom field objects
        metric = rawMetric.keys[0] ?? 'unknown_metric'
      } else if (typeof rawMetric === 'string') {
        metric = rawMetric
      }

      let label = ''
      let key = ''

      if (metric.includes('post') && agg.fn === 'count') {
        label = 'Volume'
        key = 'volume'
      } else if (metric.includes('sentiment') && agg.fn === 'avg') {
        label = 'Sentiment'
        key = 'sentiment'
      } else if (metric.includes('score') && agg.fn === 'avg') {
        label = 'Score'
        key = 'score'
      } else {
        // For custom fields like MSRP, use the field name as both label and key
        label = metric === 'unknown_metric' ? `${agg.fn}(${metric})` : metric
        key =
          metric === 'unknown_metric' ? `metric_${index}` : metric.toLowerCase()
      }

      return { label, key, index }
    })
  }

  // Fallback to default metrics if no aggregatedValues provided
  return [
    { label: 'Volume', key: 'volume', index: 0 },
    { label: 'Sentiment', key: 'sentiment', index: 1 },
  ]
}

// Generate a title based on the identifier
const generateTitleFromIdentifier = (
  identifier: ChartIdentifier | undefined,
): string => {
  if (!identifier || !identifier.domains) {
    return 'Multi-Domain Analysis'
  }

  const domains = identifier.domains
  const primaryDomain = domains[0]

  if (!primaryDomain) {
    return 'Multi-Domain Analysis'
  }

  // Extract the field name from the domain
  const rawField = primaryDomain.args?.[0] || 'data'
  let fieldName = 'data'

  // Handle custom field objects
  if (
    typeof rawField === 'object' &&
    Array.isArray(rawField?.keys) &&
    rawField?.keys.length > 0
  ) {
    // For custom fields, use the key name (e.g., "Aerosol Size (oz)")
    fieldName = rawField.keys[0] ?? 'data'
  } else if (typeof rawField === 'string') {
    fieldName = rawField
  }

  // Convert field names to readable titles
  const fieldMappings: { [key: string]: string } = {
    product_brand: 'Brand',
    product_category: 'Category',
    product_name: 'Product',
    product_hierarchy: 'Product',
    source_type: 'Source Type',
    sentiment: 'Sentiment',
    rating: 'Rating',
    location: 'Location',
    country: 'Country',
    region: 'Region',
    post_created_at: 'Time',
    user_id: 'User',
    theme: 'Theme',
    topic: 'Topic',
    cluster_topic: 'Theme',
  }

  const readableField =
    fieldMappings[fieldName] ||
    fieldName
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (l: string) => l.toUpperCase())

  // Check if there are time-based domains for trend analysis
  const hasTimeDomain = domains.some((domain) => {
    const domainArg = domain.args?.[0]
    // Check if it's a time-based function
    if (domain.fn?.includes('dt_bucket')) {
      return true
    }
    // Check if the argument is a string and includes 'created_at'
    if (typeof domainArg === 'string' && domainArg.includes('created_at')) {
      return true
    }
    // For custom field objects, check if any key includes 'created_at'
    if (
      typeof domainArg === 'object' &&
      domainArg !== null &&
      'keys' in domainArg &&
      domainArg.keys
    ) {
      return domainArg.keys.some((key: string) => key.includes('created_at'))
    }
    return false
  })

  if (hasTimeDomain) {
    return `${readableField} Analysis Over Time`
  }

  return `${readableField} Analysis`
}

const MultiDomainChart: FC<MultiDomainChartProps> = memo(
  ({
    identifier,
    data,
    title,
    type,
    aggregatedValues,
    conversationUuid,
    messageUuid,
    filters,
    fetchChart,
  }) => {
    const projectId = useProjectStore((state: ProjectState) => state.projectId)
    const chartRef = useRef<HTMLDivElement>(null)
    const availableMetrics = useMemo(
      () => getAvailableMetrics(aggregatedValues),
      [aggregatedValues],
    )

    const generatedTitle = useMemo(
      () => title || generateTitleFromIdentifier(identifier),
      [title, identifier],
    )
    const [primaryDomain, setPrimaryDomain] = useState(
      availableMetrics[0]?.key || 'volume',
    )
    const [chartType, setChartType] = useState(type || 'bar')
    const [currentPage, setCurrentPage] = useState(0)
    const [infoModalOpen, setInfoModalOpen] = useState(false)
    const itemsPerPage = 10

    const timeIdentifier = convertToSnakeCase({
      aggregatedValues: [...identifier.aggregatedValues],
      constraints: [...identifier.constraints],
      domains: [
        ...identifier.domains,
        {
          args: [{ dimension: 'post_created_at' }],
          fn: 'dt_bucket_month',
        },
      ],
    } as unknown as JsonValue)

    const { data: timeChartData } = useQuery<{
      data: DataPoint[]
    }>(
      ['chart-data', timeIdentifier],
      () =>
        fetchChart(projectId, timeIdentifier) as Promise<{
          data: DataPoint[]
        }>,
      {
        enabled: !!timeIdentifier,
        refetchOnWindowFocus: false,
      },
    )

    // Sort data before pagination
    const sortedData = useMemo(() => {
      const selectedMetricIndex =
        availableMetrics.find((m) => m.key === primaryDomain)?.index || 0
      return [...data].sort(
        (a, b) =>
          (b.values[selectedMetricIndex] ?? 0) -
          (a.values[selectedMetricIndex] ?? 0),
      )
    }, [data, primaryDomain, availableMetrics])

    // Pagination logic
    const totalPages = Math.ceil(sortedData.length / itemsPerPage)
    const startIndex = currentPage * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    const paginatedData = sortedData.slice(startIndex, endIndex)

    const colorMap = useMemo(
      () => generateOptimalColors(data.map((item) => item.category[0]!)),
      [data],
    )

    const activeChartData =
      chartType === 'bar' ? paginatedData : timeChartData?.data || []

    const isLoading = !activeChartData

    const chartOptions = useMemo(
      () => ({
        primaryDomain,
        showLegend: true,
        availableMetrics,
        selectedMetricIndex:
          availableMetrics.find((m) => m.key === primaryDomain)?.index || 0,
        allData: sortedData, // Pass all data for scale calculation
      }),
      [primaryDomain, availableMetrics, sortedData],
    )

    // Pagination handlers
    const handlePreviousPage = (): void => {
      setCurrentPage((prev) => Math.max(0, prev - 1))
    }

    const handleNextPage = (): void => {
      setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1))
    }

    // Reset to first page when changing metrics or chart type
    useEffect(() => {
      setCurrentPage(0)
    }, [primaryDomain, chartType])

    // Add this handler function
    const handleInfoModalClose = (): void => {
      setInfoModalOpen(false)
    }

    // Export functions
    const exportAsImage = (): void => {
      if (chartRef.current) {
        const canvas = chartRef.current.querySelector('canvas')
        if (canvas) {
          const link = document.createElement('a')
          link.download = `${generatedTitle.replace(/\s+/g, '_')}_chart.png`
          link.href = canvas.toDataURL('image/png')
          link.click()
          toast.success('Chart image downloaded')
        }
        mixpanel.track('askyogi', {
          action: 'download_chart_png',
          conversation_uuid: conversationUuid,
          message_uuid: messageUuid,
        })
      }
    }

    // Export functions
    const copyChartImageToClipboard = (): void => {
      if (chartRef.current) {
        const canvas = chartRef.current.querySelector('canvas')
        if (canvas) {
          canvas.toBlob(function (blob: Blob | null) {
            if (!blob) {
              return
            }
            const item = new ClipboardItem({ 'image/png': blob ?? '' })
            navigator.clipboard.write([item])
            toast.success('Chart copied to clipboard')
          })
        }
        mixpanel.track('askyogi', {
          action: 'copy_chart_png',
          conversation_uuid: conversationUuid,
          message_uuid: messageUuid,
        })
      }
    }

    const exportAsCSV = (): void => {
      // Use sortedData (all data) instead of activeChartData (current page only)
      const dataToExport =
        chartType === 'bar' ? sortedData : timeChartData?.data || []

      const csvData = dataToExport.map((item: DataPoint) => {
        const row: Record<string, string | number> = {
          Category: item.category[0] ?? '',
        }

        if (chartType === 'line' && item.category.length > 1) {
          row['Date'] = item.category[1] ?? ''
        }

        availableMetrics.forEach((metric, index) => {
          row[metric.label] = item.values[index] ?? 0
        })
        return row
      })

      // Include Date in headers for line charts
      const headers =
        chartType === 'line' &&
        dataToExport.length > 0 &&
        dataToExport[0]!.category.length > 1
          ? ['Category', 'Date', ...availableMetrics.map((m) => m.label)]
          : ['Category', ...availableMetrics.map((m) => m.label)]

      const csvContent = [
        headers.join(','),
        ...csvData.map((row: Record<string, string | number>) =>
          headers.map((header) => row[header] ?? '').join(','),
        ),
      ].join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `${generatedTitle.replace(/\s+/g, '_')}_data.csv`
      link.click()
      toast.success('Chart data downloaded')
      mixpanel.track('askyogi', {
        action: 'download_chart_csv',
        conversation_uuid: conversationUuid,
        message_uuid: messageUuid,
      })
    }

    const DownloadOptions = (): JSX.Element => (
      <DownloadContainer>
        <DownloadItem onClick={copyChartImageToClipboard}>
          Copy chart to clipboard
        </DownloadItem>
        <DownloadItem onClick={exportAsImage}>
          Download chart as PNG
        </DownloadItem>
        <DownloadItem onClick={exportAsCSV}>Download data as CSV</DownloadItem>
      </DownloadContainer>
    )

    const hasPagination = chartType === 'bar' && totalPages > 1

    return (
      <Container>
        <Header>
          <Title>{generatedTitle}</Title>
          <HeaderActions>
            <HeaderAction onClick={() => setInfoModalOpen(true)}>
              <InfoOutlined />
            </HeaderAction>
            <Popover
              content={<DownloadOptions />}
              side="left"
              align="start"
              nativeButton={false}
            >
              <HeaderAction>
                <DownloadRounded />
              </HeaderAction>
            </Popover>
          </HeaderActions>
        </Header>

        <ControlsContainer>
          <RadioGroup
            aria-label="Chart type"
            variant="button"
            value={chartType}
            onValueChange={setChartType}
          >
            <Radio value={'bar'} aria-label="Bar chart">
              <BarChartOutlined style={{ transform: 'rotate(90deg)' }} />
            </Radio>
            <Radio value={'line'} aria-label="Line chart">
              <SsidChartOutlined />
            </Radio>
          </RadioGroup>
          <RadioGroup
            aria-label="Metric"
            variant="button"
            value={primaryDomain}
            onValueChange={setPrimaryDomain}
          >
            {availableMetrics.map((metric) => (
              <Radio key={metric.key} value={metric.key}>
                {metric.label === 'Score' ? 'Rating' : metric.label}
              </Radio>
            ))}
          </RadioGroup>
        </ControlsContainer>

        <ChartContainer ref={chartRef}>
          <ChartWrapper>
            {isLoading ? (
              <Spin></Spin>
            ) : (
              <ChartV2
                data={activeChartData}
                type={chartType}
                chartOptions={chartOptions}
                colorMap={colorMap}
              />
            )}
          </ChartWrapper>
        </ChartContainer>

        <div style={{ paddingTop: 10, paddingBottom: hasPagination ? 20 : 10 }}>
          <InfoText>
            {chartType === 'line'
              ? `Hover over data points to see all metric values`
              : totalPages > 1
                ? `Showing ${availableMetrics.find((m) => m.key === primaryDomain)?.label || primaryDomain} data (${startIndex + 1}-${Math.min(endIndex, sortedData.length)} of ${sortedData.length} items)`
                : `Bar chart shows ${availableMetrics.find((m) => m.key === primaryDomain)?.label || primaryDomain} data sorted by magnitude`}
          </InfoText>
        </div>
        {hasPagination && (
          <PaginationContainer>
            <PaginationButton
              onClick={handlePreviousPage}
              disabled={currentPage === 0}
            >
              <ArrowBack />
            </PaginationButton>

            <PageInfo>
              Page {currentPage + 1} of {totalPages} ({startIndex + 1}-
              {Math.min(endIndex, sortedData.length)} of {sortedData.length}{' '}
              items)
            </PageInfo>

            <PaginationButton
              onClick={handleNextPage}
              disabled={currentPage === totalPages - 1}
            >
              <ArrowForward />
            </PaginationButton>
          </PaginationContainer>
        )}

        <ChartInfoModal
          open={infoModalOpen}
          onClose={handleInfoModalClose}
          identifier={identifier}
          filters={filters}
        />
      </Container>
    )
  },
)

MultiDomainChart.displayName = 'MultiDomainChart'

export default MultiDomainChart

const Container = styled.div`
  width: 100%;
  background-color: white;
  border-radius: 16px;
  border: 1px solid #28364f26;
  display: flex;
  flex-direction: column;
  overflow: hidden; /* Prevent content overflow */
  padding-bottom: 20px;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0;
  position: relative;
  z-index: 10;
  background-color: white;
  border-bottom: 1px solid #28364f26;
  padding: 10px 20px;
  color: #28364f;
`

const ControlsContainer = styled.div`
  display: flex;
  //justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  color: #28364f;
  //background-color: #f8f9fa;
  //border-bottom: 1px solid #28364f26;
  gap: 16px;
`

const Title = styled.h3`
  margin: 0;
  color: #333;
  font-size: 16px;
  font-weight: 600;
`

const ChartContainer = styled.div`
  margin-bottom: 16px;
  position: relative;
  z-index: 1;
  height: 400px;
  overflow: hidden;
`

const ChartWrapper = styled.div`
  margin-top: 0;
  width: 100%;
  height: 100%;
`

const InfoText = styled.p`
  margin: 0;
  color: #666;
  font-size: 14px;
  text-align: center;
  font-style: italic;
`

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px 0px;
  border-top: 1px solid #28364f26;
`

const PaginationButton = styled.button<{ disabled: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 16px;
  border: 1px solid #eeeff3;
  border-radius: 6px;
  background: #fff;
  color: #a9afb9;
  font-weight: 500;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    //background-color: #eeeff3;
    color: #28364f;
  }

  &:disabled {
    opacity: 0.5;
  }
`

const PageInfo = styled.span`
  color: #666;
  font-size: 14px;
  font-weight: 500;
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const HeaderAction = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 8px;
  color: rgba(40, 54, 79, 0.8);
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  &:hover {
    background-color: #eeeff3;
    opacity: 1;
  }
`

const DownloadContainer = styled.div`
  display: flex;
  padding: 4px 0;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  border: 1px solid #ebebeb;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15);
`
const DownloadItem = styled.div`
  display: flex;
  padding: 8px 16px;
  align-items: center;
  gap: 12px;
  align-self: stretch;
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    background-color: #eeeff3;
  }
`
